import { alpha, Box, type SxProps, type Theme, useTheme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { ALPHA, ICON_TILE, ICON_TILE_DASHED, INK, NEUTRAL, SURFACE } from "@/theme/tokens";

export type AccentIconTileSize = "xs" | "sm" | "md" | "lg";

/**
 * A semantic tile colouring: `primary` is the brand tile, `neutral` a quiet grey one, the status tones their palette's
 * tint with the palette's dark ink — for a tile whose colour says which BUCKET a thing belongs to.
 */
export type AccentIconTileTone = "primary" | "neutral" | "info" | "success" | "warning" | "error";

type Props = {
	/** Footprint from the `ICON_TILE` token: `xs` ~28px, `sm` ~40px, `md` ~48px, `lg` ~56px. */
	size: AccentIconTileSize;
	/** Glyph (foreground) color. Defaults to the brand accent (`theme.palette.primary.main`). */
	color?: string;
	/** Tile background. Defaults to a brand-tinted wash (`alpha(primary, ALPHA.BRAND_TINT)`). Pass a categorical tint to re-color the tile. */
	tint?: string;
	/**
	 * Colours the glyph and tile from a semantic tone instead of the brand accent. Explicit `color`/`tint` still win.
	 * Absent = the brand tile, unchanged.
	 */
	tone?: AccentIconTileTone;
	/**
	 * Draws the tile as a dashed outline on the surface, glyph in the muted ink — for a thing whose bucket isn't known (a
	 * change with no recorded cause). Wins over `tone`; explicit `color` still wins. Absent = the filled tile, unchanged.
	 */
	dashed?: boolean;
	children: ReactNode;
	/**
	 * Root element. Defaults to a `div`; pass `"span"` when the tile sits inside PHRASING content —
	 * a `<button>` row (`SystemChoiceList`'s `content` slot), a `<label>`, a `<p>` — where a `div`
	 * is invalid markup and browsers silently restructure the DOM around it.
	 */
	component?: "div" | "span";
	/**
	 * Already-translated name, for a tile that stands ALONE — a logo in a table cell with no text beside it.
	 * Optional and additive: when set, the tile is announced as an image with this name (`role="img"`) and shows
	 * it as a tooltip on hover, so its meaning never rests on colour or shape alone. Omitted, the tile stays
	 * decorative as before — it sits next to text that already says what it is.
	 */
	label?: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

const TILE_BY_SIZE: Record<AccentIconTileSize, { size: number; glyph: string }> = {
	xs: ICON_TILE.XS,
	sm: ICON_TILE.SM,
	md: ICON_TILE.MD,
	lg: ICON_TILE.LG,
};

/**
 * The single reusable "identity tile": a rounded-square, brand-tinted background with a
 * centered glyph in the accent color. Replaces the hand-copied
 * `Box{ p, borderRadius, bgcolor: tint, color: accent, display: 'flex' }` pattern that had
 * drifted across section headers, panel headers, and list-item / lever markers.
 *
 * Fully generic: geometry comes from the `ICON_TILE` token (`size`), and both `color` +
 * `tint` are overridable so a categorical lever can tint itself while the default is the
 * theme brand accent (so it re-tints when the org changes its primary color). The glyph
 * inherits its size from the token via `& > svg`, but a child icon with its own `fontSize`
 * still wins — so callers can keep a bespoke glyph size where needed.
 */
const toneColors = (theme: Theme, tone: AccentIconTileTone): { accent: string; background: string } =>
	tone === "neutral"
		? { accent: INK.SECONDARY, background: NEUTRAL[150] }
		: { accent: theme.palette[tone].dark, background: alpha(theme.palette[tone].main, ALPHA.BRAND_TINT) };

const DASHED_COLORS = { accent: INK.MUTED, background: SURFACE } as const;

export const AccentIconTile: FC<Props> = ({ size, color, tint, tone, dashed, children, component = "div", label, dataTestId, sx }) => {
	const theme = useTheme();
	const { size: px, glyph } = TILE_BY_SIZE[size];
	const toned = dashed === true ? DASHED_COLORS : tone === undefined ? null : toneColors(theme, tone);
	const accent = color ?? toned?.accent ?? theme.palette.primary.main;
	const background = tint ?? toned?.background ?? alpha(theme.palette.primary.main, ALPHA.BRAND_TINT);
	const tile = (
		<Box
			component={component}
			data-testid={dataTestId}
			role={label === undefined ? undefined : "img"}
			aria-label={label}
			sx={[
				{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
					width: px,
					height: px,
					borderRadius: `${px / 4}px`,
					backgroundColor: background,
					color: accent,
					...(dashed === true && { border: `${ICON_TILE_DASHED.BORDER_PX}px dashed ${NEUTRAL[450]}`, boxSizing: "border-box" }),
					"& > svg": { fontSize: glyph },
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{children}
		</Box>
	);
	return label === undefined ? tile : <InHouseTooltip title={label}>{tile}</InHouseTooltip>;
};
